import React, { useEffect, useRef } from 'react';
import { Zap, Users, MessageCircle, DollarSign, Target, Shield, CheckCircle, TrendingUp } from 'lucide-react';
import SectionHeader from './SectionHeader';
import useStats from '../hooks/useStats';
import AnimatedNumber from './AnimatedNumber';

const benefits = [
  {
    icon: Zap,
    title: 'Fast Hiring',
    stat: '< 24hrs',
    description: 'Connect with the right talent in minutes, not days',
    gradient: 'from-yellow-500 to-orange-400'
  },
  {
    icon: Users,
    title: 'Verified Talent',
    stat: '2K+',
    description: 'Work with verified freelancers and background checks',
    gradient: 'from-blue-500 to-cyan-400'
  },
  {
    icon: MessageCircle,
    title: 'Easy Communication',
    stat: '100%',
    description: 'Real-time chat, video calls, and file sharing',
    gradient: 'from-green-500 to-teal-400'
  },
  {
    icon: DollarSign,
    title: 'Affordable',
    stat: '0% Fee',
    description: 'Transparent pricing with secure payment protection',
    gradient: 'from-emerald-500 to-green-400'
  },
  {
    icon: Target,
    title: 'AI Matching',
    stat: '98%',
    description: 'Advanced algorithms ensure perfect project matches',
    gradient: 'from-purple-500 to-pink-400'
  },
  {
    icon: Shield,
    title: 'Secure',
    stat: 'Bank-Level',
    description: 'Enterprise security with escrow protection',
    gradient: 'from-indigo-500 to-purple-400'
  }
];

const WhyChooseUs = () => {
  const sectionRef = useRef(null);
  const { stats, loading } = useStats();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.benefit-card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-fade-in-up');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-choose-us"
      className="py-12 md:py-12 relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 left-1/4 h-80 w-80 rounded-full bg-blue-500/10 blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-cyan-500/10 blur-[120px] animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <SectionHeader
          eyebrow="Why Choose Kaamhub"
          title="The Smartest Way to"
          accent="Hire & Get Hired"
          description="Join thousands of successful professionals who trust Kaamhub."
          className="mb-14 md:mb-16"
        />

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div
                key={index}
                className="benefit-card opacity-0 group relative"
              >
                <div className="h-full rounded-3xl border border-slate-200/80 bg-white/85 p-8 backdrop-blur-sm hover:border-blue-200 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900/75 dark:hover:border-cyan-800">
                  {/* Icon & Stat */}
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className={`text-right`}>
                      <div className={`text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${benefit.gradient}`}>
                        {benefit.title === 'Verified Talent' && stats
                          ? <AnimatedNumber value={stats.users} duration={1200} />
                          : benefit.stat}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Hover Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-500 pointer-events-none`}></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats Bar */}
        <div className="rounded-3xl border border-slate-200/80 bg-white/85 p-8 shadow-2xl backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/80 md:p-12">
          <div className="grid gap-8 text-center md:grid-cols-4">
            <div>
              <div className="mb-2 flex items-center justify-center">
                <CheckCircle className="mr-2 h-6 w-6 text-cyan-500" />
                <div className="text-4xl font-bold text-gray-900 dark:text-white md:text-5xl">
                  {loading ? '...' : stats ? <AnimatedNumber value={stats.users} /> : '2K+'}
                </div>
              </div>
              <div className="font-medium text-gray-600 dark:text-gray-400">Active Users</div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-center">
                <TrendingUp className="mr-2 h-6 w-6 text-cyan-500" />
                <div className="text-4xl font-bold text-gray-900 dark:text-white md:text-5xl">
                  {loading ? '...' : stats ? <AnimatedNumber value={stats.posts} /> : '2.5K+'}
                </div>
              </div>
              <div className="font-medium text-gray-600 dark:text-gray-400">Job Postings</div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-center">
                <Users className="mr-2 h-6 w-6 text-cyan-500" />
                <div className="text-4xl font-bold text-gray-900 dark:text-white md:text-5xl">
                  {loading ? '...' : stats ? <AnimatedNumber value={stats.applications} /> : '1K+'}
                </div>
              </div>
              <div className="font-medium text-gray-600 dark:text-gray-400">Applications</div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-center">
                <Target className="mr-2 h-6 w-6 text-cyan-500" />
                <div className="text-4xl font-bold text-gray-900 dark:text-white md:text-5xl">4.5★</div>
              </div>
              <div className="font-medium text-gray-600 dark:text-gray-400">Average Rating</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
